import { Settings } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { InstitutionalHoldersChartDisplayMode } from '@/utils/institutionalHoldersChartDisplayMode';

const MODES: { id: InstitutionalHoldersChartDisplayMode; label: string; hint: string }[] = [
  {
    id: 'ownership',
    label: 'Ownership',
    hint: 'Largest stakes at quarter-end',
  },
  {
    id: 'activity',
    label: 'Activity',
    hint: 'Biggest share adds to largest sells',
  },
];

export function InstitutionalHoldersLayerSettings({
  displayMode,
  onDisplayModeChange,
  layerActive,
}: {
  displayMode: InstitutionalHoldersChartDisplayMode;
  onDisplayModeChange: (mode: InstitutionalHoldersChartDisplayMode) => void;
  layerActive: boolean;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (rootRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (!layerActive) return null;

  return (
    <div ref={rootRef} className="relative shrink-0">
      <button
        type="button"
        aria-label="Institutional holders display settings"
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="flex h-6 w-6 items-center justify-center rounded-md text-emerald-700 transition-colors hover:bg-emerald-50 focus:outline-none focus-visible:ring-1 focus-visible:ring-emerald-400"
      >
        <Settings className="h-3.5 w-3.5" />
      </button>

      {open ? (
        <div
          role="dialog"
          aria-label="Institutional holders settings"
          className="absolute left-0 top-full z-50 mt-1.5 w-52 rounded-lg border border-slate-200 bg-white p-2 shadow-lg"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <p className="px-2 pb-1.5 text-[10px] font-medium uppercase tracking-wide text-slate-400">
            Chart columns
          </p>
          <div className="flex flex-col gap-0.5">
            {MODES.map((mode) => {
              const selected = displayMode === mode.id;
              return (
                <button
                  key={mode.id}
                  type="button"
                  onClick={() => {
                    onDisplayModeChange(mode.id);
                    setOpen(false);
                  }}
                  className={`rounded-md px-2 py-1.5 text-left transition-colors ${
                    selected ? 'bg-emerald-50 text-emerald-900' : 'text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  <span className="block text-xs font-medium">{mode.label}</span>
                  <span className="block text-[10px] text-slate-500">{mode.hint}</span>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
